import { openWorkspaceDb, type WorkspaceDb } from '@/offline/db'
import { nowUtcIso } from '@/lib/dates'
import { deriveKey, encryptChunk, encodeText, randomBytes, sha256Hex, toB64 } from './crypto'
import {
  BACKUP_FORMAT,
  BACKUP_TABLES,
  CHUNK_BYTES,
  FORMAT_VERSION,
  KDF_ITERATIONS,
  SCHEMA_VERSION,
  type BackupBundle,
  type BackupRecords,
  type BackupTable,
} from './manifest'

/** Read every ledger record for a workspace straight out of Dexie. */
export async function collectRecords(db: WorkspaceDb, workspaceId: string): Promise<BackupRecords> {
  const out = {} as BackupRecords
  for (const table of BACKUP_TABLES) {
    out[table] = await db.table(table).where('workspace_id').equals(workspaceId).toArray()
  }
  return out
}

/** Latest updated_at across all records = the snapshot watermark (plan §127). */
function computeWatermark(records: BackupRecords): string | null {
  let max: string | null = null
  for (const table of BACKUP_TABLES) {
    for (const row of records[table] as { updated_at?: string }[]) {
      const ts = row?.updated_at
      if (typeof ts === 'string' && (max === null || ts > max)) max = ts
    }
  }
  return max
}

/**
 * Build a fully encrypted, self-verifying backup bundle. The recovery secret is
 * used only in-memory to derive the key; it is never written into the bundle.
 */
export async function buildBackupBundle(workspaceId: string, secret: string): Promise<BackupBundle> {
  const db = openWorkspaceDb(workspaceId)
  const records = await collectRecords(db, workspaceId)
  const plaintext = JSON.stringify(records)
  const plaintextBytes = encodeText(plaintext)
  const recordsSha = await sha256Hex(plaintextBytes)

  const salt = randomBytes(16)
  const key = await deriveKey(secret, salt, KDF_ITERATIONS)

  const chunks: BackupBundle['chunks'] = []
  for (let offset = 0, index = 0; offset < plaintextBytes.length || index === 0; offset += CHUNK_BYTES, index++) {
    const slice = plaintextBytes.slice(offset, offset + CHUNK_BYTES)
    const { nonce, ciphertext } = await encryptChunk(key, slice)
    chunks.push({
      index,
      nonce: toB64(nonce),
      ciphertext: toB64(ciphertext),
      plaintext_sha256: await sha256Hex(slice),
      plaintext_bytes: slice.length,
    })
    if (offset + CHUNK_BYTES >= plaintextBytes.length) break
  }

  const counts = {} as Record<BackupTable, number>
  for (const table of BACKUP_TABLES) counts[table] = records[table].length

  return {
    format: BACKUP_FORMAT,
    format_version: FORMAT_VERSION,
    schema_version: SCHEMA_VERSION,
    workspace_id: workspaceId,
    created_at: nowUtcIso(),
    watermark: computeWatermark(records),
    kdf: { algo: 'PBKDF2', hash: 'SHA-256', iterations: KDF_ITERATIONS, salt: toB64(salt) },
    cipher: { algo: 'AES-GCM', length: 256, tag_length: 128 },
    counts,
    records_sha256: recordsSha,
    plaintext_bytes: plaintextBytes.length,
    chunks,
  }
}

export function bundleToJson(bundle: BackupBundle): string {
  return JSON.stringify(bundle, null, 2)
}

export function bundleToBlob(bundle: BackupBundle): Blob {
  return new Blob([bundleToJson(bundle)], { type: 'application/json' })
}

export function backupFilename(bundle: BackupBundle): string {
  const stamp = bundle.created_at.slice(0, 19).replace(/[:T]/g, '-')
  return `mtp-backup-${stamp}.mtpbak`
}
